import { useState } from "react";
import RequestSelect from "./RequestSelect";
import DateRangePickerDialog from "components/Dialog/DateRangePickerDialog";
import intl from "components/i18n/ReactIntlWrapper";
import StyledDropzone from "../TimeOffFileUploader";
import WorkflowActionsBar from "../../Workflow/WorkflowActionsBar";
import TypeAhead from "components/common/TypeAhead";
import { useApi } from "../../../api/ApiProvider";
import Api from "../../../axiosApi/api";
import { PermissionsGate } from "../../authorization/PermissionsGate";
import { ENTITY_TYPE } from "common/constants";
import { IEditingModeSetings } from "../../Workflow/WorkflowActionsBar";
import { isEditableByOwner } from "components/authorization/PermissionUtils";
import { Box, Grid, Typography, Chip, useTheme, Stack, Card, CardContent } from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import ErrorSummary from "components/common/ErrorSummary";

const TimeOffRequestForm = ({
  requestType,
  input,
  setInput,
  errors,
  loading,
  handleRequestChange,
  handleDelete,
  handleDownload,
  handleDaySelect,
  handleSave,
  handleEmployeeChange,
  HRView,
  disabled,
  setComments = null,
}) => {
  const [wfEditSettings, setWfEditSettings] = useState<IEditingModeSetings>();
  const theme = useTheme();
  const api: Api = useApi();
  const handleEditingMode = (params: IEditingModeSetings) => {
    setWfEditSettings(params);
  };
  return (
    <Stack spacing={3}>
      <Card>
        <CardContent>
          <Grid container spacing={2} mb={4}>
            <Grid size={{xs:12}}>
              <ErrorSummary errors={errors} showEverything={true} />
            </Grid>

            <Grid size={{xs:12, md: input.id > 0 ? 6 : 12 }}>
              <Typography
                variant="subtitle2"
                className={errors.IdEmployeeTimeOffType ? "text-danger" : ""}
                gutterBottom
              >
                {intl.get("timeOff.type")}
              </Typography>
              <RequestSelect
                requestType={requestType}
                selectedRequest={input?.idEmployeeRegimeTimeOffType}
                handleRequestChange={handleRequestChange}
                disabled={
                  !(
                    wfEditSettings?.edit ||
                    isEditableByOwner(
                      ENTITY_TYPE.EMPLOYEE,
                      input.idEmployee,
                      wfEditSettings
                    )
                  ) || disabled
                }
              />
            </Grid>
            {input.id > 0 && (
              <Grid size={{xs:12, md: 6}} mb={4}>
                <Typography variant="subtitle2" gutterBottom>
                  {intl.get("timeOffApprove.status.label")}
                </Typography>
                <Chip
                  variant="outlined"
                  label={
                    <Typography className="statusLabel">
                      {intl.get(input.workFlowLabelKey)}
                    </Typography>
                  }
                  sx={{
                    backgroundColor: input.workflowStateColor || "#fff",
                    color: theme.palette.getContrastText(input.workflowStateColor || "#fff"),
                    px: 2,
                    py: 1,
                  }}
                />
              </Grid>
            )}
          </Grid>

          <Grid container spacing={2} mb={4}>
            <Grid size={{xs:12}}>
              <Typography
                variant="subtitle2"
                className={errors.range ? "text-danger" : ""}
                gutterBottom
              >
                {intl.get("timeOff.time.range")}
              </Typography>
              <DateRangePickerDialog
                disabled={
                  !(
                    wfEditSettings?.edit ||
                    isEditableByOwner(
                      ENTITY_TYPE.EMPLOYEE,
                      input.idEmployee,
                      wfEditSettings
                    )
                  ) || disabled
                }
                inputName="dateRange"
                className="timesheet-datepicker"
                selected={input.range}
                setRange={handleDaySelect}
                errors={errors}
                required
              />
            </Grid>
          </Grid>
              
      {HRView && (
        <PermissionsGate
          identifier="ui.timeoff.request-form.rrhh-section"
          entityType={ENTITY_TYPE.EMPLOYEE}
          viewRoles={[]}
          editRoles={[]}
        >
          <Grid mb={4}>
              <Typography variant="subtitle2" gutterBottom>
                {intl.get("timeOffRequestForm.employeeName")}
              </Typography>
              <TypeAhead
                apiPromise={(term) =>
                  api.employeeApi.apiVversionListEmployeesTypeAheadGet('1', term).then(res => ({
                    ...res,
                    data: { ...res.data, data: res.data?.data?.queryResult ?? [] }
                  }))
                }
                getOptionLabel={(option: any) => option?.label || ''}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                value={
                  input.idEmployee && input.employeeName
                    ? { id: input.idEmployee, label: `${input.employeeLastName}, ${input.employeeName}` }
                    : null
                }
                onChange={(_event, newValue) => handleEmployeeChange(newValue)}
                disabled={
                  !(
                    wfEditSettings?.edit ||
                    isEditableByOwner(
                      ENTITY_TYPE.EMPLOYEE,
                      input.idEmployee,
                      wfEditSettings
                    )
                  ) || disabled
                }
                disableClearable={true}
                autocompleteProps={{ size: 'small' }}
              />
              </Grid>
        </PermissionsGate>
      )}
          <StyledDropzone
            files={input.attachments}
            setInput={setInput}
            disabled={
              !(
                wfEditSettings?.edit ||
                isEditableByOwner(
                  ENTITY_TYPE.EMPLOYEE,
                  input.idEmployee,
                  wfEditSettings
                )
              ) || disabled
            }
          />
          
      {input.attachments?.length > 0 && (
            <Grid mb={4}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              {intl.get("timeOffApprove.attachedFiles.label")}
            </Typography>
            {input.attachments.map((file, i) => (
              <Box key={i} display="flex" alignItems="center" gap={1} mb={1}>
                <Typography color="primary">
                  {file.path || file}
                </Typography>
                <a href={file.path} download>
                  <DownloadOutlinedIcon
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleDownload(file);
                    }}
                  />
                </a>
                <CancelOutlinedIcon
                  color="error"
                  style={{ cursor: "pointer" }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDelete(i, file);
                  }}
                />
              </Box>
            ))}
            </Grid>
          )}
            </CardContent>
          </Card>

          <Grid container spacing={2} alignItems="center" justifyContent="space-between" mb={4}>
            <Grid size={{md: 6}}>
              <Typography>{intl.get("timeOff.mandatory.field")}</Typography>
            </Grid>
            <Grid size={{md: 6}}>
              <WorkflowActionsBar
                loading={loading}
                viewMode="buttons"
                handleEditingMode={handleEditingMode}
                onActionClick={handleSave}
                id={input.id}
                stateId={input.idWorkflowState}
                disabled={
                  !input.idEmployeeRegimeTimeOffType ||
                  !input.range ||
                  Object.keys(errors).length > 0
                }
              />
            </Grid>
          </Grid>
    </Stack>

  );
};

export default TimeOffRequestForm;
